import type { IClassificationRepository } from "@/application/ports/repositories";
import type {
  ClassificationResponse,
  ClassificationListResponse,
  ClassificationListParams,
  ClassificationExportParams,
  ClassificationSummaryResponse,
  PipelineStatusBreakdown,
  SuppliersAnalyticsResponse,
  TaxBreakdownAnalyticsResponse,
  WorkforceAnalyticsResponse,
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  SignUploadBatchRequest,
  SignUploadBatchResponse,
  UploadSessionStatusResponse,
  ReviewClassificationRequest,
  ClassificationFileResponse,
  ClassificationLocksResponse,
  FieldLockInput,
} from "@/domain/classification";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { apiClient, ApiClientError } from "../client";
import { downloadBlob } from "../fileDownload";
import { buildExportPreviewUrl, buildExportDownloadUrl } from "../exportUrls";
import { getLogger } from "@/infrastructure/di/services";

export class ClassificationRepository implements IClassificationRepository {
  private basePath(beneficiaryUuid: string): string {
    return `/beneficiaries/${beneficiaryUuid}/classifications`;
  }

  private buildUrl(path: string, params: URLSearchParams): string {
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  private validateResponse<T>(data: unknown, requiredKeys: string[]): T {
    if (!data || typeof data !== "object") {
      throw new ApiClientError({
        message: "Invalid response format",
        status: 500,
        code: "INVALID_RESPONSE",
        details: { received: data },
      });
    }
    const missingKeys = requiredKeys.filter((key) => !(key in (data as object)));
    if (missingKeys.length > 0) {
      throw new ApiClientError({
        message: "Response missing required fields",
        status: 500,
        code: "INCOMPLETE_RESPONSE",
        details: { missingKeys, received: data },
      });
    }
    return data as T;
  }

  private handleError(error: unknown, operation: string): never {
    getLogger().error(`Classification ${operation} failed:`, error);
    if (error instanceof ApiClientError) throw error;
    if (error instanceof Error) {
      throw new ApiClientError({
        message: error.message || `Failed to ${operation}`,
        status: 0,
        code: "NETWORK_ERROR",
        details: { originalError: error.message },
      });
    }
    throw new ApiClientError({
      message: `An unexpected error occurred during ${operation}`,
      status: 0,
      code: "UNKNOWN_ERROR",
      details: { error: String(error) },
    });
  }

  async listClassifications(
    beneficiaryUuid: string,
    params?: ClassificationListParams,
  ): Promise<ClassificationListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.status) queryParams.append("status", params.status);
        if (params.document_type) {
          const types = Array.isArray(params.document_type)
            ? params.document_type
            : [params.document_type];
          for (const dt of types) {
            queryParams.append("document_type", dt);
          }
        }
        if (params.search) queryParams.append("search", params.search);
        if (params.eligible !== undefined)
          queryParams.append("eligible", params.eligible.toString());
        if (params.expense_type) queryParams.append("expense_type", params.expense_type);
        // F4 filters
        if (params.eligibility) queryParams.append("eligibility", params.eligibility);
        if (params.requires_review !== undefined)
          queryParams.append("requires_review", params.requires_review ? "true" : "false");
        if (params.rule_id) queryParams.append("rule_id", params.rule_id);
        if (params.fiscal_quota_uuid)
          queryParams.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
        if (params.fiscal_category && params.fiscal_category.length > 0) {
          for (const cat of params.fiscal_category) {
            queryParams.append("fiscal_category", cat);
          }
        }
        if (params.parent_document_uuid)
          queryParams.append("parent_document_uuid", params.parent_document_uuid);
        if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
        if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());
        if (params.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params.sort_direction) queryParams.append("sort_direction", params.sort_direction);
      }

      const base = this.basePath(beneficiaryUuid);
      const url = this.buildUrl(base, queryParams);

      const response = await apiClient.get<ClassificationListResponse>(url);
      return this.validateResponse<ClassificationListResponse>(response.data, [
        "classifications",
        "total_count",
        "limit",
        "offset",
      ]);
    } catch (error) {
      throw this.handleError(error, `list classifications for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getTypeCounts(
    beneficiaryUuid: string,
    params?: {
      status?: string;
      document_type?: string | string[];
      search?: string;
      eligible?: boolean;
      expense_type?: string;
      fiscal_quota_uuid?: string;
      eligibility?: string;
      fiscal_category?: string[];
    },
  ): Promise<Record<string, number>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append("status", params.status);
      if (params?.document_type) {
        const types = Array.isArray(params.document_type)
          ? params.document_type
          : [params.document_type];
        for (const dt of types) {
          queryParams.append("document_type", dt);
        }
      }
      if (params?.search) queryParams.append("search", params.search);
      if (params?.eligible !== undefined)
        queryParams.append("eligible", params.eligible.toString());
      if (params?.expense_type) queryParams.append("expense_type", params.expense_type);
      if (params?.fiscal_quota_uuid)
        queryParams.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
      if (params?.eligibility) queryParams.append("eligibility", params.eligibility);
      if (params?.fiscal_category && params.fiscal_category.length > 0) {
        for (const fc of params.fiscal_category) {
          queryParams.append("fiscal_category", fc);
        }
      }

      const url = this.buildUrl(`${this.basePath(beneficiaryUuid)}/stats`, queryParams);
      const response = await apiClient.get<Record<string, number>>(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error, `get type counts for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationResponse> {
    try {
      const response = await apiClient.get<ClassificationResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}`,
      );
      return this.validateResponse<ClassificationResponse>(response.data, [
        "uuid",
        "status",
        "source_filename",
      ]);
    } catch (error) {
      throw this.handleError(error, `get classification ${classificationUuid}`);
    }
  }

  async getClassificationFile(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationFileResponse> {
    try {
      const response = await apiClient.get<ClassificationFileResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}/file`,
      );
      return this.validateResponse<ClassificationFileResponse>(response.data, ["signed_url"]);
    } catch (error) {
      throw this.handleError(error, `get file for classification ${classificationUuid}`);
    }
  }

  async getSummary(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
    status?: string,
    documentType?: string,
    eligible?: boolean,
    expenseType?: string,
    eligibility?: string,
    fiscalCategory?: string[],
  ): Promise<ClassificationSummaryResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (fiscalQuotaUuid) queryParams.append("fiscal_quota_uuid", fiscalQuotaUuid);
      if (status) queryParams.append("status", status);
      if (documentType) queryParams.append("document_type", documentType);
      if (eligible !== undefined) queryParams.append("eligible", eligible.toString());
      if (expenseType) queryParams.append("expense_type", expenseType);
      if (eligibility) queryParams.append("eligibility", eligibility);
      if (fiscalCategory && fiscalCategory.length > 0) {
        for (const fc of fiscalCategory) {
          queryParams.append("fiscal_category", fc);
        }
      }

      const base = `${this.basePath(beneficiaryUuid)}/summary`;
      const url = this.buildUrl(base, queryParams);

      const response = await apiClient.get<ClassificationSummaryResponse>(url);
      return this.validateResponse<ClassificationSummaryResponse>(response.data, [
        "financials",
        "document_inventory",
        "conciliation",
        "monthly",
        "categories",
      ]);
    } catch (error) {
      throw this.handleError(error, `get summary for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getPipelineStatus(beneficiaryUuid: string): Promise<PipelineStatusBreakdown> {
    try {
      const url = `${this.basePath(beneficiaryUuid)}/pipeline-status`;
      const response = await apiClient.get<PipelineStatusBreakdown>(url);
      return this.validateResponse<PipelineStatusBreakdown>(response.data, [
        "queued",
        "processing",
        "failed",
        "classified",
        "needs_review",
        "duplicate_detected",
      ]);
    } catch (error) {
      throw this.handleError(error, `get pipeline status for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getSuppliersAnalytics(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
    expenseType?: string,
  ): Promise<SuppliersAnalyticsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (fiscalQuotaUuid) queryParams.append("fiscal_quota_uuid", fiscalQuotaUuid);
      if (expenseType) queryParams.append("expense_type", expenseType);

      const base = `${this.basePath(beneficiaryUuid)}/analytics/suppliers`;
      const url = this.buildUrl(base, queryParams);

      const response = await apiClient.get<SuppliersAnalyticsResponse>(url);
      return this.validateResponse<SuppliersAnalyticsResponse>(response.data, [
        "suppliers",
        "total_suppliers",
        "top3_concentration",
      ]);
    } catch (error) {
      throw this.handleError(error, `get suppliers analytics for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getTaxBreakdown(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
    expenseType?: string,
  ): Promise<TaxBreakdownAnalyticsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (fiscalQuotaUuid) queryParams.append("fiscal_quota_uuid", fiscalQuotaUuid);
      if (expenseType) queryParams.append("expense_type", expenseType);

      const base = `${this.basePath(beneficiaryUuid)}/analytics/tax-breakdown`;
      const url = this.buildUrl(base, queryParams);

      const response = await apiClient.get<TaxBreakdownAnalyticsResponse>(url);
      return this.validateResponse<TaxBreakdownAnalyticsResponse>(response.data, [
        "total_iva",
        "total_irpf",
        "iva_by_rate",
      ]);
    } catch (error) {
      throw this.handleError(error, `get tax breakdown for beneficiary ${beneficiaryUuid}`);
    }
  }

  async getWorkforceAnalytics(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
  ): Promise<WorkforceAnalyticsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (fiscalQuotaUuid) queryParams.append("fiscal_quota_uuid", fiscalQuotaUuid);

      const base = `${this.basePath(beneficiaryUuid)}/analytics/workforce`;
      const url = this.buildUrl(base, queryParams);

      const response = await apiClient.get<WorkforceAnalyticsResponse>(url);
      return this.validateResponse<WorkforceAnalyticsResponse>(response.data, [
        "total_payroll",
        "worker_count",
        "workers",
      ]);
    } catch (error) {
      throw this.handleError(error, `get workforce analytics for beneficiary ${beneficiaryUuid}`);
    }
  }

  async createUploadSession(
    beneficiaryUuid: string,
    data: CreateUploadSessionRequest,
  ): Promise<CreateUploadSessionResponse> {
    try {
      const response = await apiClient.post<CreateUploadSessionResponse>(
        `${this.basePath(beneficiaryUuid)}/upload-session`,
        data,
        { timeout: 60_000 },
      );
      return this.validateResponse<CreateUploadSessionResponse>(response.data, [
        "session_id",
        "expires_at",
        "uploads",
      ]);
    } catch (error) {
      throw this.handleError(error, `create upload session for beneficiary ${beneficiaryUuid}`);
    }
  }

  async signUploadBatch(
    beneficiaryUuid: string,
    sessionUuid: string,
    data: SignUploadBatchRequest,
  ): Promise<SignUploadBatchResponse> {
    try {
      const response = await apiClient.post<SignUploadBatchResponse>(
        `${this.basePath(beneficiaryUuid)}/upload-session/${sessionUuid}/signed-urls`,
        data,
        { timeout: 60_000 },
      );
      return this.validateResponse<SignUploadBatchResponse>(response.data, [
        "session_id",
        "expires_at",
        "uploads",
      ]);
    } catch (error) {
      throw this.handleError(error, `sign upload batch for session ${sessionUuid}`);
    }
  }

  async getUploadSessionStatus(
    beneficiaryUuid: string,
    sessionUuid: string,
  ): Promise<UploadSessionStatusResponse> {
    try {
      const response = await apiClient.get<UploadSessionStatusResponse>(
        `${this.basePath(beneficiaryUuid)}/sessions/${sessionUuid}`,
      );
      return this.validateResponse<UploadSessionStatusResponse>(response.data, [
        "session_id",
        "status",
        "expected_count",
      ]);
    } catch (error) {
      throw this.handleError(error, `get upload session status ${sessionUuid}`);
    }
  }

  async updateClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: Record<string, unknown>,
  ): Promise<ClassificationResponse> {
    try {
      const response = await apiClient.patch<ClassificationResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}`,
        data,
      );
      return this.validateResponse<ClassificationResponse>(response.data, [
        "uuid",
        "status",
        "source_filename",
      ]);
    } catch (error) {
      throw this.handleError(error, `update classification ${classificationUuid}`);
    }
  }

  async reviewClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: ReviewClassificationRequest,
  ): Promise<ClassificationResponse> {
    try {
      const response = await apiClient.patch<ClassificationResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}/review`,
        data,
      );
      return this.validateResponse<ClassificationResponse>(response.data, [
        "uuid",
        "status",
        "source_filename",
      ]);
    } catch (error) {
      throw this.handleError(error, `review classification ${classificationUuid}`);
    }
  }

  async resolveDuplicate(
    beneficiaryUuid: string,
    data: { keep_uuid: string; discard_uuid: string },
  ): Promise<{
    kept: ClassificationResponse;
    discarded: ClassificationResponse;
  }> {
    try {
      const response = await apiClient.post(
        `${this.basePath(beneficiaryUuid)}/resolve-duplicate`,
        data,
      );
      return this.validateResponse<{
        kept: ClassificationResponse;
        discarded: ClassificationResponse;
      }>(response.data, ["kept", "discarded"]);
    } catch (error) {
      throw this.handleError(error, "resolve duplicate");
    }
  }

  async deleteClassification(beneficiaryUuid: string, classificationUuid: string): Promise<void> {
    try {
      await apiClient.delete(`${this.basePath(beneficiaryUuid)}/${classificationUuid}`);
    } catch (error) {
      throw this.handleError(error, `delete classification ${classificationUuid}`);
    }
  }

  async bulkDeleteClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<{ deleted_uuids: string[]; missing_uuids: string[] }> {
    try {
      const response = await apiClient.post(`${this.basePath(beneficiaryUuid)}/bulk-delete`, {
        classification_uuids: classificationUuids,
      });
      return this.validateResponse<{
        deleted_uuids: string[];
        missing_uuids: string[];
      }>(response.data, ["deleted_uuids", "missing_uuids"]);
    } catch (error) {
      throw this.handleError(error, "bulk delete classifications");
    }
  }

  async reclassifyClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    locks?: FieldLockInput[],
  ): Promise<ClassificationResponse> {
    try {
      // Body only when locks are provided: omit to preserve existing sticky
      // locks, send `{ locks }` to replace the set (empty array clears it).
      const response = await apiClient.post<ClassificationResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}/reclassify`,
        locks === undefined ? undefined : { locks },
      );
      return this.validateResponse<ClassificationResponse>(response.data, ["uuid", "status"]);
    } catch (error) {
      throw this.handleError(error, `reclassify classification ${classificationUuid}`);
    }
  }

  async getClassificationLocks(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationLocksResponse> {
    try {
      const response = await apiClient.get<ClassificationLocksResponse>(
        `${this.basePath(beneficiaryUuid)}/${classificationUuid}/locks`,
      );
      return this.validateResponse<ClassificationLocksResponse>(response.data, [
        "locks",
        "lockable_fields",
      ]);
    } catch (error) {
      throw this.handleError(error, `get classification locks ${classificationUuid}`);
    }
  }

  async bulkReclassifyClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<{
    reclassified_uuids: string[];
    skipped_uuids: string[];
    missing_uuids: string[];
  }> {
    try {
      const response = await apiClient.post(`${this.basePath(beneficiaryUuid)}/bulk-reclassify`, {
        classification_uuids: classificationUuids,
      });
      return this.validateResponse<{
        reclassified_uuids: string[];
        skipped_uuids: string[];
        missing_uuids: string[];
      }>(response.data, ["reclassified_uuids", "skipped_uuids", "missing_uuids"]);
    } catch (error) {
      throw this.handleError(error, "bulk reclassify classifications");
    }
  }

  async downloadClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    filename: string,
  ): Promise<void> {
    try {
      const fileResponse = await this.getClassificationFile(beneficiaryUuid, classificationUuid);

      const response = await fetch(fileResponse.signed_url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: { Accept: "application/pdf,image/*" },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      throw this.handleError(error, `download classification ${classificationUuid}`);
    }
  }

  async previewExport(params: ClassificationExportParams): Promise<ExportPreview> {
    const url = buildExportPreviewUrl(
      this.basePath(params.beneficiary_uuid),
      classificationExportParamsToQuery(params),
    );
    const response = await apiClient.get<ExportPreview>(url);
    return response.data;
  }

  async downloadExport(
    params: ClassificationExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const url = buildExportDownloadUrl(
      this.basePath(params.beneficiary_uuid),
      classificationExportParamsToQuery(params),
      options,
    );
    const { blob, filename } = await apiClient.getBlobWithFilename(
      url,
      `classifications.${options.format}`,
    );
    /**
     * Preset flows (e.g. "Solicitar correcciones al promotor") pass a
     * custom filename so the operator gets a self-describing file name
     * instead of the generic ``classifications_<timestamp>.xlsx`` the
     * backend produces.
     */
    downloadBlob(blob, options.filenameOverride ?? filename);
  }
}

function classificationExportParamsToQuery(params: ClassificationExportParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.status) query.append("status", params.status);
  if (params.document_type) {
    for (const dt of params.document_type) query.append("document_type", dt);
  }
  if (params.search) query.append("search", params.search);
  if (params.eligibility) query.append("eligibility", params.eligibility);
  if (params.requires_review !== undefined)
    query.append("requires_review", String(params.requires_review));
  if (params.rule_id) query.append("rule_id", params.rule_id);
  if (params.fiscal_quota_uuid) query.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
  if (params.fiscal_category) {
    for (const fc of params.fiscal_category) query.append("fiscal_category", fc);
  }
  if (params.expense_type) query.append("expense_type", params.expense_type);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_direction) query.append("sort_direction", params.sort_direction);
  return query;
}
